import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { PageHeader, useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import type { LocationSuggestion, PickedLocation } from '../../../components/maps/LocationPicker';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { AdminPage, FormActions, FormSection, FullRow, apiError } from '../shared/AdminUi';

// El mapa (MapLibre) se descarga solo al abrir este formulario.
const LocationPicker = React.lazy(() => import('../../../components/maps/LocationPicker'));

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface ClientForm {
  idNumber: string;
  fullName: string;
  phone: string;
  mobilePhone: string;
  mainAddress: string;
  secondaryAddress: string;
  landmark: string;
  job: string;
  coSignerFullName: string;
  coSignerIdNumber: string;
  coSignerAddress: string;
  coSignerPhone: string;
  officeId: string;
  routeId: string;
}

const EMPTY: ClientForm = {
  idNumber: '',
  fullName: '',
  phone: '',
  mobilePhone: '',
  mainAddress: '',
  secondaryAddress: '',
  landmark: '',
  job: '',
  coSignerFullName: '',
  coSignerIdNumber: '',
  coSignerAddress: '',
  coSignerPhone: '',
  officeId: '',
  routeId: '',
};

const REQUIRED: (keyof ClientForm)[] = ['idNumber', 'fullName', 'phone', 'mobilePhone', 'mainAddress', 'job'];

/** Editar cliente: sede y ruta, datos, ubicación y codeudor. */
const ClientEdit: React.FC<ShowAlerts> = () => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams();
  const { canUpdate } = useResourcePermissions('/admin/clients');
  const [form, setForm] = useState<ClientForm>(EMPTY);
  const [initial, setInitial] = useState<ClientForm>(EMPTY);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof ClientForm, string>>>({});
  const [saving, setSaving] = useState(false);
  const [location, setLocation] = useState<PickedLocation | null>(null);
  const [savedSource, setSavedSource] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<LocationSuggestion>(null);
  const [locationChanged, setLocationChanged] = useState(false);
  const [locationLoaded, setLocationLoaded] = useState(false);

  setPageTitle('pageTitle.clientEdit');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`clients/${id}`);
        const c = data.client;
        const loaded: ClientForm = {
          idNumber: String(c.idNumber ?? ''),
          fullName: c.fullName ?? '',
          phone: String(c.phone ?? ''),
          mobilePhone: String(c.mobilePhone ?? ''),
          mainAddress: c.mainAddress ?? '',
          secondaryAddress: c.secondaryAddress ?? '',
          landmark: c.landmark ?? '',
          job: c.job ?? '',
          coSignerFullName: c.coSignerFullName ?? '',
          coSignerIdNumber: String(c.coSignerIdNumber ?? ''),
          coSignerAddress: c.coSignerAddress ?? '',
          coSignerPhone: String(c.coSignerPhone ?? ''),
          officeId: c.route?.office?.id != null ? String(c.route.office.id) : '',
          routeId: c.route?.id != null ? String(c.route.id) : '',
        };
        setInitial(loaded);
        setForm(loaded);
        try {
          const loc = await axios.get(`clients/${id}/location`);
          const saved = loc.data?.location;
          if (saved?.latitude != null && saved?.longitude != null) {
            setLocation({ latitude: Number(saved.latitude), longitude: Number(saved.longitude), source: saved.source ?? 'map', accuracyM: saved.accuracyM });
            setSavedSource(saved.source ?? null);
          }
          // La sugerencia solo se ofrece si el punto no está verificado.
          if (!saved?.verified) setSuggestion(loc.data?.suggestion ?? null);
        } catch (locationError) {
          // Sin ubicación (API anterior o sin permiso): el formulario sigue funcionando.
        }
        setLocationLoaded(true);
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('offices');
        setOffices(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        notify.warning(t('clients.noOffices'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!form.officeId) return;
    (async () => {
      try {
        const { data } = await axios.get(`offices/${form.officeId}/routes`);
        setOfficeRoutes(Array.isArray(data) ? data : []);
      } catch (error) {
        notify.warning(t('clients.noRoutes'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.officeId]);

  const set = (key: keyof ClientForm) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const dirty = locationChanged || (Object.keys(form) as (keyof ClientForm)[]).some((k) => form[k] !== initial[k]);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const found: Partial<Record<keyof ClientForm, string>> = {};
    REQUIRED.forEach((k) => {
      if (!String(form[k]).trim()) found[k] = t('common.required');
    });
    if (!form.routeId) found.routeId = t('common.required');
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(t('clients.missingFields'));
      return;
    }
    setSaving(true);
    try {
      await axios.put(`/clients/${id}`, {
        ...form,
        landmark: form.landmark.trim() || null,
        officeId: Number(form.officeId),
        routeId: Number(form.routeId),
      });
      if (locationChanged && location) {
        try {
          await axios.put(`/clients/${id}/location`, location);
        } catch (locationError) {
          notify.warning(t('clients.location.saveError'));
        }
      }
      notify.success(t('common.savedOk'));
      navigate('/admin/clients');
    } catch (error: any) {
      notify.error(apiError(error, t('common.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const err = (f: keyof ClientForm) => ({ error: !!errors[f], helperText: errors[f] });
  const title = t('clients.editTitle');

  return (
    <AdminPage narrow>
      <PageHeader
        title={title}
        description={form.fullName || undefined}
        breadcrumbs={[{ label: t('clients.title'), href: '#/admin/clients' }, { label: title }]}
      />
      <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormSection title={t('clients.officeSection')}>
          <TextField
            id="client-office"
            select
            label={t('clients.office')}
            value={offices.length ? form.officeId : ''}
            onChange={(e) => setForm((f) => ({ ...f, officeId: e.target.value, routeId: '' }))}
            required
            fullWidth
          >
            {offices.map((office) => (
              <MenuItem key={office.id} value={String(office.id)}>
                {office.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id="client-route"
            select
            label={t('clients.route')}
            value={officeRoutes.length ? form.routeId : ''}
            onChange={set('routeId')}
            required
            fullWidth
            {...err('routeId')}
          >
            {officeRoutes.map((route) => (
              <MenuItem key={route.id} value={String(route.id)}>
                {route.name}
              </MenuItem>
            ))}
          </TextField>
        </FormSection>

        <FormSection title={t('clients.clientSection')}>
          <TextField id="idNumber" label={t('clients.idNumber')} value={form.idNumber} InputProps={{ readOnly: true }} helperText={t('clients.idNumberLocked')} fullWidth />
          <TextField id="fullName" label={t('clients.fullName')} value={form.fullName} onChange={set('fullName')} inputProps={{ maxLength: 30 }} required fullWidth {...err('fullName')} />
          <TextField id="mobilePhone" type="tel" label={t('common.cellphone')} value={form.mobilePhone} onChange={set('mobilePhone')} inputProps={{ maxLength: 10 }} required fullWidth {...err('mobilePhone')} />
          <TextField id="phone" type="tel" label={t('common.phone')} value={form.phone} onChange={set('phone')} inputProps={{ maxLength: 10 }} required fullWidth {...err('phone')} />
          <TextField id="job" label={t('clients.job')} placeholder={t('clients.jobPlaceholder')} value={form.job} onChange={set('job')} inputProps={{ maxLength: 30 }} required fullWidth {...err('job')} />
        </FormSection>

        <FormSection title={t('clients.addressSection')}>
          <TextField id="mainAddress" label={t('common.address')} value={form.mainAddress} onChange={set('mainAddress')} inputProps={{ maxLength: 30 }} required fullWidth {...err('mainAddress')} />
          <TextField id="secondaddres" label={t('clients.secondAddress')} value={form.secondaryAddress} onChange={set('secondaryAddress')} inputProps={{ maxLength: 30 }} fullWidth />
          <FullRow>
            <TextField
              id="landmark"
              label={t('clients.landmark')}
              placeholder={t('clients.landmarkPlaceholder')}
              value={form.landmark}
              onChange={set('landmark')}
              inputProps={{ maxLength: 255 }}
              multiline
              minRows={2}
              fullWidth
            />
          </FullRow>
          {locationLoaded && (
            <FullRow>
              <React.Suspense fallback={null}>
                <LocationPicker
                  value={location}
                  savedSource={savedSource}
                  suggestion={suggestion}
                  onChange={(next) => {
                    setLocation(next);
                    setLocationChanged(true);
                  }}
                />
              </React.Suspense>
            </FullRow>
          )}
        </FormSection>

        <FormSection title={t('clients.coSignerSection')} description={t('clients.coSignerHint')}>
          <TextField id="coSignerFullName" label={t('clients.coSignerName')} value={form.coSignerFullName} onChange={set('coSignerFullName')} inputProps={{ maxLength: 30 }} fullWidth />
          <TextField id="coSignerIdNumber" label={t('clients.coSignerIdNumber')} value={form.coSignerIdNumber} InputProps={{ readOnly: true }} fullWidth />
          <TextField id="coSignerPhone" type="tel" label={t('clients.coSignerPhone')} value={form.coSignerPhone} onChange={set('coSignerPhone')} inputProps={{ maxLength: 10 }} fullWidth />
          <TextField id="coSignerAddress" label={t('clients.coSignerAddress')} value={form.coSignerAddress} onChange={set('coSignerAddress')} inputProps={{ maxLength: 30 }} fullWidth />
        </FormSection>

        <FormActions cancelTo="/admin/clients" saving={saving} allowed={canUpdate} deniedTooltip={t('common.noPermissionEdit')} disabled={!dirty} />
      </Box>
    </AdminPage>
  );
};

export default ClientEdit;
